import { IMapperValue } from '20.formLib/helpers/QCScriptLib/bridge/mapper';
import { QCSBaseObject } from '20.formLib/helpers/QCScriptLib/QuickConnect.QCScript/IL/QCSObject/QCSBaseObject';
import { QCSDouble } from '20.formLib/helpers/QCScriptLib/QuickConnect.QCScript/IL/QCSObject/QCSDouble';
import { BooleanExtension } from '80.quickConnect.Core/formatting/BooleanExtension';
import { DateTimeExtension } from '80.quickConnect.Core/formatting/DateTimeExtension';
import { DoubleExtension } from '80.quickConnect.Core/formatting/DoubleExtension';
import NumberExtension from '80.quickConnect.Core/formatting/NumberExtension';
import { findChoice, findChoiceInChoiceList, findChoiceInHierarchicalChoice } from '80.quickConnect.Core/helpers';
import { isQCNotification } from '80.quickConnect.Core/helpers/common';
import { DateTimeFieldType, FieldType } from '90.quickConnect.Models/enums';
import {
  mapAllFieldTypeValuesToBoolean,
  mapAllFieldTypeValuesToChoice,
  mapAllFieldTypeValuesToChoiceList,
  mapAllFieldTypeValuesToCoordinates,
  mapAllFieldTypeValuesToDateTime,
  mapAllFieldTypeValuesToNotificationData,
  mapAllFieldTypeValuesToNumeric,
  mapAllFieldTypeValuesToTime,
  mapAllFieldTypeValuesToString,
  mapAllFieldTypeToAddressData,
} from '90.quickConnect.Models/mappings';
import {
  AllFieldValueTypes,
  CheckBoxListDesc,
  Choice,
  ComboDesc,
  DateTimeDesc,
  DigitsDesc,
  FieldDesc,
  HierarchicalDesc,
  TimeDesc,
} from '90.quickConnect.Models/models';

export class MapperValue implements IMapperValue {
  mapToDateTime(nextValue: unknown): Date | undefined {
    if (nextValue instanceof Date) return nextValue;
    else if (typeof nextValue === 'string') return DateTimeExtension.parseMultiFormat(nextValue) ?? undefined;
    else if (typeof nextValue === 'number') return DateTimeExtension.mapFromNumber(nextValue) ?? undefined;
    else return;
  }

  mapToBoolean(nextValue: unknown): boolean | undefined {
    // Si la valeur est déjà un booléen, on la retourne telle quelle
    if (BooleanExtension.isBool(nextValue)) {
      return nextValue as boolean;
    }

    // Si la valeur est une chaîne de caractères, on traite les différents cas
    if (typeof nextValue === 'string') {
      const str = nextValue.trim().toLowerCase();

      const trueLabels = ['true', 'vrai'];
      const falseLabels = ['false', 'faux'];

      if (trueLabels.includes(str)) return true;
      if (falseLabels.includes(str)) return false;

      // Tentative de conversion numérique depuis la chaîne
      const num = Number(str);
      if (!isNaN(num)) return num !== 0;
    }

    // Si la valeur est un nombre
    if (typeof nextValue === 'number') {
      return nextValue !== 0;
    }

    return false;
  }

  mapToInteger(nextValue: unknown): number | undefined {
    const intValue = NumberExtension.createIntegerValue(nextValue);

    return NumberExtension.isAValidNumber(intValue) ? intValue : undefined;
  }

  mapToDouble(nextValue: unknown): number | undefined {
    const dbleValue = DoubleExtension.createDoubleValue(nextValue);

    return NumberExtension.isAValidNumber(dbleValue) ? dbleValue : undefined;
  }

  mapToChoiceList(nextValue: unknown): Choice[] | null | undefined {
    return mapAllFieldTypeValuesToChoiceList(nextValue);
  }

  mapToString(nextValue: unknown, fieldType: FieldType | undefined): string | undefined {
    return mapAllFieldTypeValuesToString(nextValue, fieldType);
  }

  mapToChoice(nextValue: unknown): Choice | null | undefined {
    return mapAllFieldTypeValuesToChoice(nextValue);
  }

  convert(
    field: FieldDesc,
    nextValue: unknown,
    instanceValue: QCSBaseObject,
    flattenFields: FieldDesc[],
  ): AllFieldValueTypes {
    const { fieldType } = field;

    let result: AllFieldValueTypes;

    switch (fieldType) {
      case FieldType.Address: {
        result = mapAllFieldTypeToAddressData(nextValue);
        break;
      }

      case FieldType.Alert:
      case FieldType.RadioList:
      case FieldType.Combo: {
        const { listChoice } = field as ComboDesc;
        const c = mapAllFieldTypeValuesToChoice(nextValue);

        if (!c) return c === null ? '' : undefined;

        if (c.value === '' && c.label === '') return c;

        result = findChoice(listChoice, c);
        break;
      }

      case FieldType.Counter:
      case FieldType.Numeric:
      case FieldType.Slider:
      case FieldType.Digits: {
        const { min, max } = field as DigitsDesc;
        const numericValue: number | null | undefined = mapAllFieldTypeValuesToNumeric(nextValue);

        if (numericValue === null) return numericValue;
        // On fait pas le check !numericValue car 0 est une valeur valide
        if (numericValue === undefined) return;

        // Cas du slider...
        if (fieldType === FieldType.Slider) result = NumberExtension.getValueInRange(numericValue, 0, 5);
        else result = NumberExtension.getValueInRange(numericValue, min, max);
        break;
      }

      case FieldType.CodeReader: {
        result = mapAllFieldTypeValuesToString(nextValue, fieldType);
        break;
      }

      case FieldType.DateTime: {
        const { min, max, type } = field as DateTimeDesc;
        const datetimeValue: Date | null | undefined = mapAllFieldTypeValuesToDateTime(nextValue);

        if (datetimeValue === null) return datetimeValue;

        if (!datetimeValue) return;

        const dtValue = DateTimeExtension.getValueInRange(datetimeValue, flattenFields, min, max);

        result = type === DateTimeFieldType.DateTime ? dtValue : DateTimeExtension.getDateFromDateTime(dtValue);
        break;
      }

      case FieldType.Time: {
        const { min, max } = field as TimeDesc;
        const timeValue: Date | null | undefined = mapAllFieldTypeValuesToTime(nextValue);

        if (timeValue === null) return timeValue;
        if (!timeValue) return;

        result = DateTimeExtension.getValueInTimeRange(timeValue, min, max);
        break;
      }

      case FieldType.HierarchicalList: {
        const { listChoice } = field as HierarchicalDesc;

        if (!listChoice) return;

        const cl = mapAllFieldTypeValuesToChoiceList(nextValue);

        if (!cl) return cl === null ? '' : undefined;

        if (cl.length === 0) return [];

        result = findChoiceInHierarchicalChoice(listChoice, cl);
        break;
      }

      case FieldType.CheckBoxList: {
        const { listChoice } = field as CheckBoxListDesc;

        if (!listChoice) return;

        const cl = mapAllFieldTypeValuesToChoiceList(nextValue);

        if (!cl) return cl === null ? '' : undefined;

        result = findChoiceInChoiceList(listChoice, cl);
        break;
      }

      case FieldType.Geolocation: {
        const geolocationValue = mapAllFieldTypeValuesToCoordinates(nextValue);

        return geolocationValue;
        break;
      }

      case FieldType.Notification: {
        const notificationDataValue = mapAllFieldTypeValuesToNotificationData(nextValue);

        if (!notificationDataValue) return;

        // Si on a une valeur existante dans le champ et que nextValue est une string
        const existingNotificationValue = isQCNotification(field.value) ? field.value : undefined;

        if (existingNotificationValue && typeof nextValue === 'string') {
          const valueTrim = nextValue.trim();
          // Si le format ne contient PAS ¤, on garde les propriétés existantes sauf selectedTargets
          if (!valueTrim.includes('¤')) {
            result = {
              ...existingNotificationValue,
              selectedTargets: notificationDataValue.selectedTargets,
            };
            break;
          }
        }

        result = notificationDataValue;
        break;
      }

      case FieldType.CheckBox: {
        result = mapAllFieldTypeValuesToBoolean(nextValue);
        break;
      }

      case FieldType.ReadOnlyValue:
        result = mapAllFieldTypeValuesToString(nextValue, fieldType);
        break;

      case FieldType.RepeatableGroup:
        result = mapAllFieldTypeValuesToString(nextValue, fieldType);
        break;

      case FieldType.Text: {
        const valToMap = instanceValue instanceof QCSDouble ? new DoubleExtension(nextValue) : nextValue;
        result = mapAllFieldTypeValuesToString(valToMap, fieldType);
        break;
      }

      case FieldType.Draw:
      case FieldType.Signature:
        if (nextValue === null || nextValue === '') return nextValue;
        break;

      default:
        break;
    }

    return result;
  }
}
